import { useMemo } from 'react';
import { Person, Reservation, ServiceBooking } from '@turizi/shared';
import { Checkbox, Tooltip } from '@mantine/core';
import { IconChevronDown, IconStack2, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatPrice, sortPersons } from '@/lib/format-utils';
import { TagIcon } from '@/lib/tag-utils';
import { ServiceTitle } from './services/service-title';
import ServiceDatePopover from './service-date-popover';
import SelectTimePopover from './select-time-popover';
import ProviderSelector from './service-booking/provider-selector';
import ShowSelfAttrs from './show-self-attrs';
import { ShowPaxGroup, ShowPerson } from './snippets';
import EditReservationBookingRow from './edit-reservation-booking-row';

interface Props {
    groupKey: string;
    bookings: ServiceBooking[];
    reservation?: Reservation;
    selectedIds?: number[];
    onToggle?: (id: number) => void;
    onToggleId?: (id: number) => void;
    showServiceOrder?: boolean;
    showReservation?: boolean;
    showClientName?: boolean;
    showPrice?: boolean;
    showCost?: boolean;
    showPaymentStatus?: boolean;
    showPaxs?: boolean;
    showPaxName?: boolean;
    showHour?: boolean;
    leftAction?: (booking: ServiceBooking) => void;
    leftSection?: React.ReactNode;
    rightAction?: (booking: ServiceBooking) => void;
    rightSection?: React.ReactNode | ((booking: ServiceBooking) => React.ReactNode);
    editable?: boolean;
    hideServicePackage?: boolean;
    isExpanded: boolean;
    onToggleExpand: () => void;
}

export default function EditReservationBookingGroupRow({
    bookings,
    reservation,
    selectedIds,
    onToggle,
    onToggleId,
    showServiceOrder = true,
    showReservation = true,
    showClientName = true,
    showPrice = false,
    showCost = false,
    showPaymentStatus = true,
    showPaxs = true,
    showPaxName = true,
    showHour = true,
    leftAction,
    leftSection,
    rightAction,
    rightSection,
    editable = false,
    hideServicePackage = true,
    isExpanded,
    onToggleExpand,
}: Props): JSX.Element | null {
    const { t } = useTranslation();
    const firstBooking = bookings[0];

    if (!firstBooking) return null;

    const service = firstBooking.service_data;
    const isPackage = service?.child_services?.length > 0;

    const totalPaxs = useMemo(() => {
        return bookings.reduce(
            (acc, b) => {
                acc.adults += b.adults || 0;
                acc.children += b.children || 0;
                acc.seniors += b.seniors || 0;
                acc.infants += b.infants || 0;
                acc.guests += b.guests || 0;
                return acc;
            },
            { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 }
        );
    }, [bookings]);

    const aggregatedPaxGroups = useMemo(() => {
        const groupMap = new Map<string | number, {
            key: string | number;
            name: string;
            personsMap: Map<string | number, Person>;
        }>();

        bookings.forEach((b) => {
            (b.pax_groups || []).forEach((bg) => {
                const paxGroup = bg.pax_group || reservation?.pax_groups?.find((rg) => rg.id === bg.pax_group_id);
                const groupKey = bg.pax_group_id || paxGroup?.id || bg.custom_name || paxGroup?.name || bg.id || 'default';
                const groupName = (bg.custom_name || paxGroup?.name || '').trim();

                if (!groupMap.has(groupKey)) {
                    groupMap.set(groupKey, {
                        key: groupKey,
                        name: groupName,
                        personsMap: new Map(),
                    });
                }

                const entry = groupMap.get(groupKey)!;
                if (!entry.name && groupName) {
                    entry.name = groupName;
                }

                const hasExplicitBookingPersons = Array.isArray(bg.booking_persons) && (bg.booking_persons.length > 0 || bg.is_custom_config);
                const persons: Person[] = hasExplicitBookingPersons
                    ? (bg.booking_persons || [])
                        .map((bp) => bp.person || bp.person_data || paxGroup?.persons?.find((gp) => gp.id === bp.person_id))
                        .filter((p): p is Person => Boolean(p))
                    : (paxGroup?.persons || []);

                persons.forEach((p) => {
                    const personKey = p.id || p.document_number || p.name;
                    if (personKey && !entry.personsMap.has(personKey)) {
                        entry.personsMap.set(personKey, p);
                    }
                });
            });
        });

        return Array.from(groupMap.values())
            .map((g) => ({
                key: g.key,
                name: g.name,
                persons: sortPersons(Array.from(g.personsMap.values())),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [bookings, reservation?.pax_groups]);

    const totalPrice = useMemo(() => {
        return bookings.reduce((sum, b) => sum + (b.money_in_amount || 0), 0);
    }, [bookings]);

    const priceCurrency = firstBooking.money_in_currency || '';
    const allSamePrice = bookings.every(
        b => b.money_in_amount === firstBooking.money_in_amount && b.money_in_currency === firstBooking.money_in_currency
    );

    const totalCost = useMemo(() => {
        return bookings.reduce((sum, b) => sum + (b.money_out_amount || 0), 0);
    }, [bookings]);

    const costCurrency = firstBooking.money_out_currency || '';
    const allSameCost = bookings.every(
        b => b.money_out_amount === firstBooking.money_out_amount && b.money_out_currency === firstBooking.money_out_currency
    );

    const ids = useMemo(() => {
        return bookings.map(b => b.id).filter((id): id is number => typeof id === 'number');
    }, [bookings]);

    const minId = ids.length > 0 ? Math.min(...ids) : null;
    const maxId = ids.length > 0 ? Math.max(...ids) : null;

    const allSelected = ids.length > 0 && selectedIds && ids.every(id => selectedIds.includes(id));
    const someSelected = Boolean(
        selectedIds && ids.some(id => selectedIds.includes(id)) && !allSelected
    );

    const handleToggleAll = (e: React.MouseEvent | React.ChangeEvent) => {
        e.stopPropagation();
        const toggleFn = onToggle || onToggleId;
        if (!toggleFn) return;

        if (allSelected) {
            ids.forEach(id => {
                if (selectedIds?.includes(id)) {
                    toggleFn(id);
                }
            });
        } else {
            ids.forEach(id => {
                if (!selectedIds?.includes(id)) {
                    toggleFn(id);
                }
            });
        }
    };

    return (
        <div className="flex flex-col gap-1.5">

            {/* Accordion Summary Header Card */}
            <div
                className={`group relative isolate ${!isExpanded ? 'mb-2 mr-2' : ''}`}
                onClick={onToggleExpand}
            >
                {!isExpanded && (
                    <>
                        <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-xl bg-gray-100/70 border border-gray-200/80 transition-transform duration-200 group-hover:translate-x-2.5 group-hover:translate-y-2.5 pointer-events-none shadow-xs" />
                        <div className="absolute inset-0 translate-x-1 translate-y-1 rounded-xl bg-gray-50/90 border border-gray-200/90 transition-transform duration-200 group-hover:translate-x-1.5 group-hover:translate-y-1.5 pointer-events-none shadow-xs" />
                    </>
                )}

                <div
                    className={`
                        relative z-10 rounded-xl p-2.5 transition-all cursor-pointer select-none
                        border
                        ${isExpanded
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200 shadow-2xs group-hover:border-gray-300'
                        }
                    `}
                >
                    {/* Booking Count Badge */}
                    {/* <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xxs font-semibold bg-slate-100/90 text-slate-600 border border-slate-200/70 shrink-0 mb-2 group-hover:bg-slate-200/60 transition-colors">
                        <IconStack2 size={12} stroke={2} className="shrink-0 text-slate-500" />
                        <span>{bookings.length} {t('bookings')}</span>
                        <div className="ml-auto text-slate-400 group-hover:text-slate-600">
                            <IconChevronDown
                                size={12}
                                stroke={2}
                                className={`transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180 text-indigo-600' : ''}`}
                            />
                        </div>
                    </div> */}

                    <div className="flex flex-col gap-1.5">
                        {/* Top Row: Checkbox, Count badge, Date/Time, Service Title, Provider, IDs, Expand Button */}
                        <div className="flex flex-col md:flex-row items-start justify-between gap-x-4 gap-y-2 border-b border-indigo-100/80 pb-1.5">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
                                {(onToggle || onToggleId) && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            size="xs"
                                            checked={Boolean(allSelected)}
                                            indeterminate={Boolean(someSelected)}
                                            onChange={handleToggleAll}
                                        />
                                    </div>
                                )}

                                {/* Date & Time */}
                                {!isPackage && (
                                    <div className="flex items-start gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <ServiceDatePopover
                                            icon={true}
                                            size="xs"
                                            serviceCategory={service?.category}
                                            value={[firstBooking.start_day, firstBooking.end_day]}
                                            onChange={() => { }}
                                            className="font-bold text-gray-800"
                                            disabled={true}
                                        />
                                        <SelectTimePopover
                                            icon={true}
                                            size="xs"
                                            value={firstBooking.start_hour}
                                            onChange={() => { }}
                                            disabled={true}
                                        />
                                    </div>
                                )}

                                {/* Service Category & Title */}
                                <div className="flex items-start gap-1 text-xs min-w-0 font-bold">
                                    <TagIcon
                                        name={service?.category}
                                        size="xs"
                                        hideText
                                        className="shrink-0 mt-0.5"
                                    />
                                    <div className='flex gap-1'>
                                        <ServiceTitle service={service} hideProviders />
                                        <div className="font-bold text-xxs solid-box-border-dark shadow-sm">
                                            x{bookings.length}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Top right actions & ID range */}
                            <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
                                {!isPackage && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <ProviderSelector booking={firstBooking} size="xs" editable={false} />
                                    </div>
                                )}

                                {minId !== null && (
                                    <Tooltip
                                        label={ids.map(id => `#${id}`).join(', ')}
                                        withArrow
                                        multiline
                                        disabled={ids.length <= 1}
                                    >
                                        <span className="text-xxs font-bold text-gray-400 bg-gray-100 hover:bg-gray-200 px-1.5 py-0.5 rounded cursor-default shrink-0">
                                            {minId === maxId ? `#${minId}` : `#${minId} - #${maxId}`}
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        {/* Bottom Row: Pax summary, Attributes, Cost, Price */}
                        <div className="sm:pl-[140px] flex flex-col lg:flex-row gap-2 items-start lg:items-center justify-between min-w-0 pt-0.5">
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0 flex-1">
                                <ShowPaxGroup
                                    adults={totalPaxs.adults}
                                    children={totalPaxs.children}
                                    seniors={totalPaxs.seniors}
                                    infants={totalPaxs.infants}
                                    guests={totalPaxs.guests}
                                />
                                {showPaxName && aggregatedPaxGroups.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] min-w-0">
                                        {aggregatedPaxGroups.map((group) => (
                                            <div key={group.key} className="inline-flex flex-wrap items-center gap-1.5 min-w-0 bg-gray-100 px-1 py-0.5 rounded-md border border-gray-200">
                                                {group.persons.length !== 1 && (
                                                    <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                                                        <IconUsersGroup size={11} className="shrink-0 text-indigo-500" />
                                                        <span className="break-words">{group.name}</span>
                                                    </span>
                                                )}
                                                {group.persons.map((p) => (
                                                    <ShowPerson person={p} key={p.id} />
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Self Attrs */}
                                <ShowSelfAttrs
                                    showkey={false}
                                    className="flex flex-wrap gap-1 min-w-0 max-w-full"
                                    attrs={firstBooking.self_attrs}
                                    context={firstBooking}
                                    size="xs"
                                />
                            </div>

                            {/* Prices & Costs */}
                            <div className="flex flex-wrap items-center justify-start lg:justify-end gap-3 text-xs shrink-0">
                                {showCost && (
                                    <div className="flex items-baseline gap-1 whitespace-nowrap">
                                        <span className="text-[10px] text-red-300 uppercase font-semibold">{t('cost')}:</span>
                                        <span className="text-red-750 font-extrabold">${formatPrice(totalCost)}</span>
                                        <span className="text-gray-500">{costCurrency}</span>
                                        {allSameCost && bookings.length > 1 && totalCost > 0 && (
                                            <span className="text-[10px] text-gray-400">
                                                (${formatPrice(firstBooking.money_out_amount || 0)} {costCurrency} {t('unit_price')})
                                            </span>
                                        )}
                                    </div>
                                )}

                                {showPrice && (
                                    <div className="flex items-baseline gap-1 whitespace-nowrap">
                                        <span className="text-[10px] text-blue-400 uppercase font-semibold">{t('price')}:</span>
                                        <span className="text-blue-600 font-extrabold">${formatPrice(totalPrice)}</span>
                                        <span className="text-gray-500">{priceCurrency}</span>
                                        {allSamePrice && bookings.length > 1 && totalPrice > 0 && (
                                            <span className="text-[10px] text-gray-400">
                                                (${formatPrice(firstBooking.money_in_amount || 0)} {priceCurrency} {t('unit_price')})
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded bookings container */}
            {isExpanded && (
                <div className="ml-2 sm:ml-4 pl-2 sm:pl-3 border-l-2 border-indigo-300/80 flex flex-col gap-2 py-1">
                    {bookings.map((booking) => (
                        <EditReservationBookingRow
                            key={booking.id}
                            booking={booking}
                            reservation={reservation}
                            selected={selectedIds?.includes(booking.id!)}
                            onToggle={onToggle || onToggleId}
                            showServiceOrder={showServiceOrder}
                            showReservation={showReservation}
                            showClientName={showClientName}
                            showPrice={showPrice}
                            showCost={showCost}
                            showPaymentStatus={showPaymentStatus}
                            showPaxs={showPaxs}
                            showPaxName={showPaxName}
                            showHour={showHour}
                            leftAction={leftAction}
                            leftSection={leftSection}
                            rightAction={rightAction}
                            rightSection={rightSection}
                            editable={editable}
                            hideServicePackage={hideServicePackage}
                            slim={false}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
